import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { readStoredWorkspaceIdentity } from 'src/workspaces/AdminWorkspaceProvider';
import { getAdminWorkspaceContext } from 'src/queries/tenant/api';
import { parseMultiCompanySuccessEnvelope } from 'src/contracts/multiCompany/v1';

/** Resolve only the verified Admin company; public documents use their own owner instead. */
export default function useCompanyActionConfiguration() {
  const identity = readStoredWorkspaceIdentity();
  const { pathname } = useLocation();
  const principal = identity.kind === 'realm' ? identity.principal : null;
  const isCompanyAdmin = principal?.role === 'COMPANY_ADMIN';
  const workspace = useQuery({
    queryKey: ['admin-workspace-context', principal?.id],
    queryFn: getAdminWorkspaceContext,
    enabled: isCompanyAdmin,
    retry: false
  });
  const explicit = /^\/platform\/companies\/([1-9]\d*)(?:\/|$)/.exec(pathname);
  const verifiedWorkspace = isCompanyAdmin && workspace.data?.actorId === principal?.id ? workspace.data : null;
  const companyId = verifiedWorkspace?.company.id ?? (principal?.role === 'SUPER_ADMIN' && explicit ? Number(explicit[1]) : null);
  const routeBase = verifiedWorkspace?.routeBase ?? (companyId ? `/platform/companies/${companyId}` : null);
  const selection = useQuery({
    queryKey: ['company-action-theme', principal?.id, companyId],
    enabled: Boolean(companyId && routeBase),
    queryFn: async () => {
      const response = await axios.get(`${routeBase}/profile/table-themes/selection`);
      const value = parseMultiCompanySuccessEnvelope<{ selected: { configuration?: unknown } | null }>(response.data);
      return value?.data?.selected?.configuration ?? null;
    },
    retry: false,
    staleTime: 30000
  });
  return companyId ? selection.data : null;
}
